import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Input, Tree } from 'antd';
import { AntTreeNodeProps } from 'antd/lib/tree';
import { AxiosResponse } from 'axios';
import { capitalCase } from 'change-case';
import { TbArrowUpRight, TbChevronDown, TbInfoCircle, TbMailShare, TbSearch, TbWorld } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { MiddleDot, PlanTag } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { EMAIL_LINKS } from '@/shared/constants/externalLinks';

interface TechnologiesProps {
    filters: any;
    handleChange: any;
}

const Technologies: FC<TechnologiesProps> = ({ filters, handleChange }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const { hasMinimumPlan } = useEntitlements();

    const [technologies, setTechnologies] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [treeData, setTreeData] = useState<any[]>([]);
    const [totalTechnologies, setTotalTechnologies] = useState(0);

    const handleGetTechnologies = async () => {
        try {
            await authorizedAxiosInstance.get(`/api/vendors/clearbit/technologies`).then((res: AxiosResponse) => {
                if (res?.status === 200) {
                    setTechnologies(res?.data);
                    setCategories(Object.keys(res?.data));
                }
            });
        } catch (error) {
            captureException(error);
        }
    };

    useEffect(() => {
        if (hasMinimumPlan('plus')) {
            handleGetTechnologies();
        }
    }, []);

    // Calculate total number of technologies (keys) across all categories
    useEffect(() => {
        const calculateTotalTechnologies = () => {
            let total = 0;

            categories.forEach((category: any) => {
                const keysInCategory = Object.keys(technologies[category] || {}).length; // Count keys in each category

                total += keysInCategory; // Add to the total count
            });

            setTotalTechnologies(total); // Update the state with the total count
        };

        if (categories.length > 0 && technologies) {
            calculateTotalTechnologies(); // Calculate when categories and technologies are populated
        }
    }, [categories, technologies]);

    // Transform the technologies into treeData format
    useEffect(() => {
        if (hasMinimumPlan('plus')) {
            const createTreeData = () => {
                const data = categories.map((category: any) => {
                    const children = Object.keys(technologies[category] || {}).map((key) => ({
                        title: key, // The name of the technology
                        key: technologies[category][key], // Unique key for the leaf
                        icon: (
                            <Avatar
                                size={16}
                                shape='square'
                                style={{ verticalAlign: '-0.2rem', marginRight: '0.25rem' }}
                                icon={<TbWorld size='0.75rem' style={{ verticalAlign: '-0.175rem' }} />}
                            />
                        ),
                        isLeaf: true, // Specify this as a leaf node
                        selectable: false,
                    }));

                    return {
                        selectable: false,
                        title: (
                            <>
                                {category && capitalCase(category)}
                                <small
                                    style={{ marginLeft: '0.5rem', color: token.colorTextTertiary, verticalAlign: 0 }}
                                >
                                    ({Object.keys(technologies[category] || {})?.length})
                                </small>
                            </>
                        ), // The category name
                        key: category, // Unique key for the parent node
                        children, // List of children (leaf nodes)
                    };
                });

                setTreeData(data); // Update the treeData state
            };

            if (categories.length > 0 && technologies) {
                createTreeData(); // Transform only when categories and technologies are populated
            }
        }
    }, [categories, technologies]);

    const [searchText, setSearchText] = useState('');

    // Filter the treeData based on search term
    const filteredTreeData = treeData
        .map((parent) => {
            const filteredChildren = parent.children.filter((child: any) =>
                child.title.toLowerCase().includes(searchText.toLowerCase()),
            );

            if (filteredChildren.length > 0) {
                return { ...parent, children: filteredChildren }; // Return parent with filtered children
            }

            return null; // If no children match the search term, don't include this category
        })
        .filter(Boolean); // Remove null values from the array

    const { token } = useAntDesignTheme();

    const [checkedKeys, setCheckedKeys] = useState({
        entireCategories: filters?.technologies?.entireCategories ?? [],
        individualTechnologies: filters?.technologies?.individualTechnologies ?? [],
    });

    return !hasMinimumPlan('plus') ? (
        <div style={{ textAlign: 'center', margin: '2rem' }}>
            <PlanTag plan={'plus'} style={{ marginLeft: '0.5rem' }} />
        </div>
    ) : (
        <>
            <PlatformAlert
                title='Technology coverage'
                showIcon
                status='info'
                icon={
                    <TbInfoCircle
                        size='1.125rem'
                        style={{
                            marginRight: '0.75rem',
                            marginTop: '0.1rem',
                        }}
                    />
                }
                description={
                    <span>
                        Filters for which technologies are used by certain companies. We use{' '}
                        <a href='http://clearbit.com' target='_blank' rel='noopener noreferrer'>
                            Clearbit
                            <TbArrowUpRight style={{ verticalAlign: '-0.125rem', marginLeft: '0.125rem' }} />
                        </a>{' '}
                        to enrich data on Countercyclical. If you see any errors, please reach out to{' '}
                        <a href={EMAIL_LINKS.engineering}>
                            us
                            <TbMailShare style={{ verticalAlign: '-0.2rem', marginLeft: '0.25rem' }} />
                        </a>
                    </span>
                }
                style={{ marginBottom: '0.75rem' }}
            />
            <Input
                placeholder='Search...'
                suffix={<TbSearch style={{ color: token.colorTextQuaternary }} />}
                value={searchText}
                onChange={(e: any) => setSearchText(e.target.value)}
            />
            {filteredTreeData?.length !== 0 && !loading ? (
                <>
                    <div style={{ margin: '0.5rem 0' }}>
                        <small>
                            <span style={{ color: token.colorTextTertiary }}>
                                {filteredTreeData?.length} Categories
                            </span>
                            <MiddleDot horizontalMargin={'0.5rem'} />
                            <span style={{ color: token.colorTextTertiary }}>
                                {totalTechnologies} Technologies Total
                            </span>
                        </small>
                    </div>
                    <Tree
                        switcherIcon={(props: AntTreeNodeProps) => {
                            return (
                                <TbChevronDown
                                    style={{
                                        transform: props.expanded ? undefined : 'rotate(-90deg)',
                                        color: token.colorTextTertiary,
                                    }}
                                />
                            );
                        }}
                        showLine
                        showIcon
                        treeData={filteredTreeData} // Pass the filtered tree data
                        checkable
                        checkedKeys={[
                            ...filters.technologies.entireCategories,
                            ...filters.technologies.individualTechnologies,
                        ]}
                        onCheck={(checkedKeysValue, info) => {
                            const newCheckedKeys = { entireCategories: [], individualTechnologies: new Set() };

                            info.checkedNodes.forEach((node: any) => {
                                if (node.children) {
                                    // If it's a parent node, add it to entireCategories
                                    newCheckedKeys.entireCategories.push(node.key as never);
                                    // Add each child to individualTechnologies, using a Set to avoid duplicates
                                    node.children.forEach((child: any) =>
                                        newCheckedKeys.individualTechnologies.add(child.key),
                                    );
                                } else {
                                    // If it's a leaf node, add it directly to individualTechnologies
                                    newCheckedKeys.individualTechnologies.add(node.key);
                                }
                            });

                            // Convert Set back to an array before updating state
                            const finalCheckedKeys = {
                                entireCategories: newCheckedKeys.entireCategories,
                                individualTechnologies: Array.from(newCheckedKeys.individualTechnologies),
                            };

                            // Update state with the latest checked categories and technologies
                            setCheckedKeys(finalCheckedKeys);

                            // Call handleChange to update defaultFilters via the parent component
                            handleChange(['technologies'], finalCheckedKeys);
                        }}
                    />
                </>
            ) : (
                loading && (
                    <div style={{ marginTop: '1rem' }}>
                        <BaseSkeleton active round paragraph={{ rows: 7 }} />
                    </div>
                )
            )}
        </>
    );
};

export default Technologies;
